import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useDebouncedCallback } from 'use-debounce'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getInviteRoleGroups } from './InviteRoleGroups'
import { Button } from '../../components/Button/Button'

const statusOptions = [
  { id: 'active', label: 'Active' },
  { id: 'deactivated', label: 'Deactivated' },
]

export interface TeamMembersFilterModel {
  namePattern: string | null
  roleNames: string[]
  statuses: string[]
}

export const defaultTeamMembersFilter: TeamMembersFilterModel = {
  namePattern: null,
  roleNames: [],
  statuses: ['active'],
}

export interface TeamMembersFilterProps {
  filter: TeamMembersFilterModel
  onFilterChanged: (filter: TeamMembersFilterModel) => void
}

const roleGroups = getInviteRoleGroups()

export const TeamMembersFilter: React.FC<TeamMembersFilterProps> = ({ filter, onFilterChanged }) => {
  const [nameInput, setNameInput] = useState(filter.namePattern ?? '')

  useEffect(() => {
    setNameInput(filter.namePattern ?? '')
  }, [filter.namePattern])

  const debouncedOnFilterChanged = useDebouncedCallback((value: string) => {
    const namePattern = value.trim() === '' ? null : value
    onFilterChanged({ ...filter, namePattern })
  }, 400)

  const handleNameInputChange = (value: string) => {
    setNameInput(value)
    debouncedOnFilterChanged(value)
  }

  const handleRoleSelectionChanged = (roleNames: string[]) => {
    onFilterChanged({ ...filter, roleNames })
  }

  const handleStatusSelectionChanged = (statuses: string[]) => {
    onFilterChanged({ ...filter, statuses })
  }

  const handleClearAll = () => {
    setNameInput('')
    debouncedOnFilterChanged.cancel()
    onFilterChanged(defaultTeamMembersFilter)
  }

  const hasActiveFilters =
    filter.namePattern !== defaultTeamMembersFilter.namePattern ||
    filter.roleNames.length !== defaultTeamMembersFilter.roleNames.length ||
    JSON.stringify(filter.statuses) !== JSON.stringify(defaultTeamMembersFilter.statuses)

  return (
    <Row className='g-3 align-items-end'>
      <Col xs={12} sm={6} lg>
        <div className='paragraph-small zocks-text-gray-900 mb-1'>Filter by Name</div>
        <Form.Control
          type='text'
          value={nameInput}
          onChange={(e) => handleNameInputChange(e.currentTarget.value)}
          placeholder='Name'
          className='paragraph-small'
        />
      </Col>
      <Col xs={12} sm={6} lg>
        <div className='paragraph-small zocks-text-gray-900 mb-1'>Filter by Role</div>
        <MultiSelectDropdown
          name='roles'
          options={roleGroups.map((role) => ({ id: role.name, label: role.name }))}
          initialSelectedOptions={filter.roleNames}
          onSelectedChanged={handleRoleSelectionChanged}
          changeOnClose
        />
      </Col>
      <Col xs={12} sm={6} lg>
        <div className='paragraph-small zocks-text-gray-900 mb-1'>Filter by Status</div>
        <MultiSelectDropdown
          name='statuses'
          options={statusOptions}
          initialSelectedOptions={filter.statuses}
          onSelectedChanged={handleStatusSelectionChanged}
          changeOnClose
        />
      </Col>
      <Col xs='auto'>
        {hasActiveFilters ? (
          <Button className='d-flex gap-1 align-items-center button-small p-0' onClick={handleClearAll}>
            <i className='bi bi-arrow-clockwise' />
            <span className='lead-small'>Clear all</span>
          </Button>
        ) : null}
      </Col>
    </Row>
  )
}
